import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useTask } from 'contexts/task';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CompletionButton,
    Stack,
    Tabs,
    TaskTile,
    Text,
    TaskCard,
} from 'shell/components';
import { getUserPreferredFullName } from 'utils/helper';

const TaskList = ({ taskList = [], subtaskList = [] }) => {
    const { currentUser } = useAuth();
    const { message } = useSnackbar();
    const { updateSubtask } = useTask();
    const { accountUsers } = useUser2();
    const { device } = useViewport();

    const [defaultRoute, setDefaultRoute] = useState('mainTask');
    const [tasks, setTasks] = useState({});
    const [subtasks, setSubtasks] = useState({});

    const renderOverdueList = (type, currentList) => {
        if (!currentList?.overdue?.length) return;
        return (
            <Stack spacing={2}>
                <Text variant={'p2'}>Overdue</Text>
                <Stack spacing={2}>
                    {currentList.overdue.map((task) =>
                        type === 'task' ? (
                            <CustomTaskCard
                                key={task.id}
                                getUserById={(ownerAccountId) =>
                                    accountUsers.find(({ owner_account_id }) => owner_account_id === ownerAccountId)
                                }
                                task={task}
                                variant={'simplified'}
                            />
                        ) : (
                            <TaskTile
                                key={task.id}
                                endComponent={
                                    <CompletionButton
                                        checked={false}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            updateSubtask(task.id, 'status', 'completed');
                                            message.success('Subtask was successfully completed.');
                                        }}
                                    />
                                }
                                getNameById={() => getUserPreferredFullName(currentUser)}
                                task={task}
                            />
                        )
                    )}
                </Stack>
            </Stack>
        );
    };

    const renderTodayList = (type, currentList) => {
        if (!currentList?.today?.length) return;
        return (
            <Stack spacing={2}>
                <Text variant={'p2'}>Today</Text>
                <Stack spacing={2}>
                    {currentList.today.map((task) =>
                        type === 'task' ? (
                            <CustomTaskCard
                                key={task.id}
                                getUserById={(ownerAccountId) =>
                                    accountUsers.find(({ owner_account_id }) => owner_account_id === ownerAccountId)
                                }
                                task={task}
                                variant={'simplified'}
                            />
                        ) : (
                            <TaskTile
                                key={task.id}
                                endComponent={
                                    <CompletionButton
                                        checked={false}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            updateSubtask(task.id, 'status', 'completed');
                                            message.success('Subtask was successfully completed.');
                                        }}
                                    />
                                }
                                getNameById={() => getUserPreferredFullName(currentUser)}
                                task={task}
                            />
                        )
                    )}
                </Stack>
            </Stack>
        );
    };

    const renderUpcomingList = (type, currentList) => {
        if (!currentList?.upcoming?.length) return;
        return (
            <Stack spacing={2}>
                <Text variant={'p2'}>Upcoming</Text>
                <Stack spacing={2}>
                    {currentList.upcoming.map((task) =>
                        type === 'task' ? (
                            <CustomTaskCard
                                key={task.id}
                                getUserById={(ownerAccountId) =>
                                    accountUsers.find(({ owner_account_id }) => owner_account_id === ownerAccountId)
                                }
                                task={task}
                                variant={'simplified'}
                            />
                        ) : (
                            <TaskTile
                                key={task.id}
                                endComponent={
                                    <CompletionButton
                                        checked={false}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            updateSubtask(task.id, 'status', 'completed');
                                            message.success('Subtask was successfully completed.');
                                        }}
                                    />
                                }
                                getNameById={() => getUserPreferredFullName(currentUser)}
                                task={task}
                            />
                        )
                    )}
                </Stack>
            </Stack>
        );
    };

    useEffect(() => {
        if (Array.isArray(taskList)) {
            taskList = taskList.filter(({ status }) => status !== 'completed');
            const todayString = moment(new Date()).format('D M YYYY');
            const findToday = taskList.filter(({ end_on }) => moment(end_on).format('D M YYYY') === todayString);
            const findOverdue = taskList
                .filter(({ end_on }) => moment(end_on).format('D M YYYY') !== todayString)
                .filter(({ end_on }) => end_on < new Date().getTime());
            const findUpcoming = taskList
                .filter(({ end_on }) => moment(end_on).format('D M YYYY') !== todayString)
                .filter(({ end_on }) => end_on > new Date().getTime());
            setTasks({
                overdue: findOverdue,
                today: findToday,
                upcoming: findUpcoming,
            });
        }
    }, [taskList]);

    useEffect(() => {
        if (Array.isArray(subtaskList)) {
            subtaskList = subtaskList.filter(({ status }) => status !== 'completed');
            const todayString = moment(new Date()).format('D M YYYY');
            const findToday = subtaskList.filter(({ end_on }) => moment(end_on).format('D M YYYY') === todayString);
            const findOverdue = subtaskList
                .filter(({ end_on }) => moment(end_on).format('D M YYYY') !== todayString)
                .filter(({ end_on }) => end_on < new Date().getTime());
            const findUpcoming = subtaskList
                .filter(({ end_on }) => moment(end_on).format('D M YYYY') !== todayString)
                .filter(({ end_on }) => end_on > new Date().getTime());
            setSubtasks({
                overdue: findOverdue,
                today: findToday,
                upcoming: findUpcoming,
            });
        }
    }, [subtaskList]);

    const tabList = [
        {
            key: 'mainTask',
            value: 'Main Task',
            content: (
                <Stack spacing={4}>
                    {tasks.overdue?.length || tasks.today?.length || tasks.upcoming?.length ? (
                        <>
                            {renderOverdueList('task', tasks)}
                            {renderTodayList('task', tasks)}
                            {renderUpcomingList('task', tasks)}
                        </>
                    ) : (
                        <Text sx={{ mt: 5 }} align={'center'} color={'light'} variant={'p2'}>
                            You don't have any tasks yet.
                        </Text>
                    )}
                </Stack>
            ),
        },
        {
            key: 'subtask',
            value: 'Subtask',
            content: (
                <Stack spacing={4}>
                    {subtasks.overdue?.length || subtasks.today?.length || subtasks.upcoming?.length ? (
                        <>
                            {renderOverdueList('subtask', subtasks)}
                            {renderTodayList('subtask', subtasks)}
                            {renderUpcomingList('subtask', subtasks)}
                        </>
                    ) : (
                        <Text sx={{ mt: 5 }} align={'center'} color={'light'} variant={'p2'}>
                            You don't have any subtasks yet.
                        </Text>
                    )}
                </Stack>
            ),
        },
    ];

    const TaskListHeader = (
        <Text variant={'sh2'} weight={'semibold'}>
            Your Tasks
        </Text>
    );

    const TaskListBody = (
        <CustomTabs
            aria-label={'select task tab'}
            options={tabList}
            selected={defaultRoute}
            onChange={setDefaultRoute}
        />
    );

    return (
        <CustomStack device={device} spacing={2}>
            {device === 'mobile' ? (
                <Accordion>
                    <AccordionSummary id={'task-list-header'} aria-controls={'task-list-content'}>
                        {TaskListHeader}
                    </AccordionSummary>
                    <AccordionDetails>{TaskListBody}</AccordionDetails>
                </Accordion>
            ) : (
                <>
                    {TaskListHeader}
                    {TaskListBody}
                </>
            )}
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        minWidth: 0,
        padding: theme.spacing(5, 5, 0),
        background: theme.palette.ensightful.shades[0],
        ...(device === 'mobile' && {
            padding: theme.spacing(5),
        }),
    };
});

const CustomTabs = styled(Tabs)(({ theme }) => {
    return {
        height: 'calc(100% - 24px - 8px)',
        '& > .MuiBox-root': {
            marginLeft: theme.spacing(-2),
            marginRight: theme.spacing(-2),
            height: 'calc(100% - 34px)',
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            paddingBottom: theme.spacing(4),
            overflowY: 'auto',
        },
    };
});

const CustomTaskCard = styled(TaskCard)(() => {
    return {
        maxWidth: 'unset',
    };
});

export default TaskList;
